import { useState } from 'react';
import {
  ShieldCheckIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { authApi } from '@/services/api';
import { LoadingSpinner } from '@/components/common';

interface TwoFactorSetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

type SetupStep = 'intro' | 'qr' | 'verify';

export default function TwoFactorSetup({ onComplete, onCancel }: TwoFactorSetupProps) {
  const [step, setStep] = useState<SetupStep>('intro');
  const [loading, setLoading] = useState(false);

  // Setup data from API
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Verify step
  const [verifyCode, setVerifyCode] = useState('');

  const handleStartSetup = async () => {
    setLoading(true);
    try {
      const response = await authApi.setup2fa();
      const data = response.data;
      setQrCodeDataUrl(data.qr_code_data_url);
      setManualSecret(data.secret_for_manual_entry);
      setBackupCodes(data.backup_codes);
      setStep('qr');
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al iniciar configuración';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    toast.success('Códigos copiados al portapapeles');
    setTimeout(() => setCopiedCodes(false), 3000);
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(manualSecret);
    toast.success('Clave copiada al portapapeles');
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Ingresa un código de 6 dígitos');
      return;
    }

    setLoading(true);
    try {
      await authApi.enable2fa(verifyCode);
      toast.success('2FA activado correctamente');
      onComplete();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Código inválido';
      toast.error(message);
      setVerifyCode('');
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Intro
  if (step === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-vida-100 rounded-full mb-4">
            <ShieldCheckIcon className="w-8 h-8 text-vida-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Activar autenticación de dos factores</h3>
          <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
            Añade una capa extra de seguridad a tu cuenta usando una app de autenticación como Google Authenticator o Authy.
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>Necesitarás:</strong> Una app de autenticación instalada en tu teléfono (Google Authenticator, Authy, Microsoft Authenticator, etc.)
          </p>
        </div>

        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-secondary flex-1">
            Cancelar
          </button>
          <button onClick={handleStartSetup} className="btn-primary flex-1" disabled={loading}>
            {loading ? <LoadingSpinner size="sm" /> : 'Comenzar'}
          </button>
        </div>
      </div>
    );
  }

  // Step 2: QR Code + Backup codes
  if (step === 'qr') {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Escanea el código QR</h3>
          <p className="text-sm text-gray-500 mt-1">
            Abre tu app de autenticación y escanea este código:
          </p>
        </div>

        {/* QR Code */}
        <div className="flex justify-center">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <img src={qrCodeDataUrl} alt="QR Code 2FA" className="w-48 h-48" />
          </div>
        </div>

        {/* Manual entry */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-2">Si no puedes escanear, ingresa esta clave manualmente:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm font-mono bg-white px-3 py-2 rounded border border-gray-200 break-all">
              {manualSecret}
            </code>
            <button
              onClick={handleCopySecret}
              className="p-2 text-gray-400 hover:text-vida-600 flex-shrink-0"
              title="Copiar"
            >
              <ClipboardDocumentIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Backup codes */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-red-800 text-sm">Códigos de respaldo</h4>
            <button
              onClick={handleCopyBackupCodes}
              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
            >
              {copiedCodes ? (
                <>
                  <CheckIcon className="w-4 h-4" />
                  Copiados
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="w-4 h-4" />
                  Copiar todos
                </>
              )}
            </button>
          </div>
          <p className="text-xs text-red-700 mb-3">
            Guarda estos códigos en un lugar seguro. Cada código solo se puede usar una vez si pierdes acceso a tu app de autenticación.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, i) => (
              <code key={i} className="text-sm font-mono bg-white px-3 py-1.5 rounded border border-red-200 text-center">
                {code}
              </code>
            ))}
          </div>
        </div>

        <button onClick={() => setStep('verify')} className="btn-primary w-full">
          He guardado los códigos, continuar
        </button>
      </div>
    );
  }

  // Step 3: Verify
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Verificar configuración</h3>
        <p className="text-sm text-gray-500 mt-1">
          Ingresa el código de 6 dígitos que muestra tu app de autenticación para confirmar la configuración.
        </p>
      </div>

      <form onSubmit={handleVerify} className="space-y-4">
        <div>
          <label className="label">Código de verificación</label>
          <input
            type="text"
            className="input text-center text-2xl tracking-widest font-mono"
            placeholder="000000"
            value={verifyCode}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setVerifyCode(val);
            }}
            autoFocus
            maxLength={6}
            autoComplete="one-time-code"
          />
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={() => setStep('qr')} className="btn-secondary flex-1">
            Volver
          </button>
          <button type="submit" className="btn-primary flex-1" disabled={loading || verifyCode.length !== 6}>
            {loading ? <LoadingSpinner size="sm" /> : 'Activar 2FA'}
          </button>
        </div>
      </form>
    </div>
  );
}
